import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';

export default function HeroSection() {
  const [isVisible, setIsVisible] = useState(false);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const handleDownloadSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError('');

    try {
      const formData = new URLSearchParams();
      formData.append('email', email);
      formData.append('request_type', 'Summit Brief Download');
      formData.append('submitted_at', new Date().toISOString());

      const response = await fetch('https://readdy.ai/api/form/d67m616a728k8ctu1v70', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData.toString(),
      });

      if (response.ok) {
        setSubmitSuccess(true);
        setEmail('');
      } else {
        setSubmitError('Something went wrong. Please try again.');
      }
    } catch {
      setSubmitError('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const closeModal = () => {
    setShowDownloadModal(false);
    setSubmitSuccess(false);
    setSubmitError('');
    setEmail('');
  };

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden bg-navy-950">
      {/* Split Background Design */}
      <div className="absolute inset-0">
        {/* Left Side - Image */}
        <div className="absolute inset-y-0 left-0 w-1/2">
          <img
            src="https://public.readdy.ai/ai/img_res/b5fd31de84bce917ea10e1b3c6069c14.jpg"
            alt="Women In Wealth Summit"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-navy-950/30 via-navy-950/70 to-navy-950"></div>
        </div>
        
        {/* Right Side - Solid Color */}
        <div className="absolute inset-y-0 right-0 w-1/2 bg-navy-950"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 lg:px-12 py-20">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left Column - Empty for image */}
          <div></div>

          {/* Right Column - Content */}
          <div className={`space-y-8 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <div className="space-y-6">
              <div className="inline-block">
                <span className="text-champagne-400 text-sm font-sans font-medium tracking-[0.2em] uppercase">
                  March 2026 · Beirut, Lebanon
                </span>
              </div>
              
              <h1 className="font-display text-6xl lg:text-7xl font-bold text-white leading-[1.1]">
                Women In<br />Wealth
              </h1>
              
              <div className="w-20 h-0.5 bg-champagne-400"></div>
              
              <p className="text-xl text-navy-100 font-sans leading-relaxed max-w-xl">
                Curated by the <strong className="text-white font-semibold">WealthRise Management team</strong>
              </p>
              
              <p className="text-lg text-navy-200 font-sans leading-relaxed max-w-xl">
                A private summit bringing together women who move capital, shape legacies, and redefine the future of global wealth.
              </p>
            </div>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <Link
                to="/attend"
                className="group relative px-8 py-4 bg-champagne-500 text-navy-950 font-sans font-semibold text-base text-center overflow-hidden transition-all duration-300 hover:bg-champagne-400 whitespace-nowrap cursor-pointer"
              >
                <span className="relative z-10">Request an Invitation</span>
                <div className="absolute inset-0 bg-champagne-400 transform translate-y-full group-hover:translate-y-0 transition-transform duration-300"></div>
              </Link>
              
              <Link
                to="/program"
                className="group px-8 py-4 border-2 border-navy-300 text-white font-sans font-semibold text-base hover:bg-navy-800 hover:border-navy-800 transition-all duration-300 whitespace-nowrap cursor-pointer text-center"
              >
                Summit Program
                <i className="ri-arrow-right-line ml-2"></i>
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
        <div className="w-6 h-10 border-2 border-navy-300 rounded-full flex items-start justify-center p-2">
          <div className="w-1 h-3 bg-champagne-400 rounded-full"></div>
        </div>
      </div>

      {/* Download Modal */}
      {showDownloadModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-navy-950/80 backdrop-blur-sm"
            onClick={closeModal}
          ></div>
          
          {/* Modal */}
          <div className="relative bg-white w-full max-w-md p-8 shadow-2xl animate-fade-in">
            {/* Close Button */}
            <button
              onClick={closeModal}
              className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-navy-400 hover:text-navy-600 transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-xl"></i>
            </button>

            {!submitSuccess ? (
              <>
                {/* Modal Header */}
                <div className="text-center mb-6">
                  <div className="w-16 h-16 mx-auto mb-4 bg-champagne-100 rounded-full flex items-center justify-center">
                    <i className="ri-file-text-line text-2xl text-champagne-600"></i>
                  </div>
                  <h3 className="font-display text-2xl font-bold text-navy-900 mb-2">
                    Download Summit Brief
                  </h3>
                  <p className="text-navy-600 text-sm">
                    Enter your email to receive the exclusive Women In Wealth Summit Brief.
                  </p>
                </div>

                {/* Form */}
                <form 
                  onSubmit={handleDownloadSubmit}
                  data-readdy-form
                  id="summit-brief-download-form"
                  className="space-y-4"
                >
                  <div>
                    <label htmlFor="download-email" className="block text-sm font-medium text-navy-700 mb-2">
                      Email Address
                    </label>
                    <input
                      type="email"
                      id="download-email"
                      name="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      placeholder="your@email.com"
                      className="w-full px-4 py-3 border border-navy-200 text-navy-900 placeholder-navy-400 focus:outline-none focus:border-champagne-500 focus:ring-1 focus:ring-champagne-500 transition-colors"
                    />
                  </div>

                  {submitError && (
                    <p className="text-red-500 text-sm">{submitError}</p>
                  )}

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full py-4 bg-navy-900 text-white font-semibold hover:bg-navy-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                  >
                    {isSubmitting ? (
                      <span className="flex items-center justify-center gap-2">
                        <i className="ri-loader-4-line animate-spin"></i>
                        Sending...
                      </span>
                    ) : (
                      <span className="flex items-center justify-center gap-2">
                        <i className="ri-download-line"></i>
                        Get the Summit Brief
                      </span>
                    )}
                  </button>
                </form>

                <p className="text-center text-xs text-navy-400 mt-4">
                  By submitting, you agree to receive communications about the summit.
                </p>
              </>
            ) : (
              /* Success State */
              <div className="text-center py-4">
                <div className="w-20 h-20 mx-auto mb-6 bg-green-100 rounded-full flex items-center justify-center">
                  <i className="ri-check-line text-3xl text-green-600"></i>
                </div>
                <h3 className="font-display text-2xl font-bold text-navy-900 mb-3">
                  Check Your Inbox
                </h3>
                <p className="text-navy-600 mb-6">
                  The Summit Brief has been sent to your email. We look forward to welcoming you in Beirut.
                </p>
                <button
                  onClick={closeModal}
                  className="px-8 py-3 bg-champagne-500 text-navy-900 font-semibold hover:bg-champagne-400 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Close
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
